import { Ref } from './Ref';
import { ObjectKeys, range } from '.';

export type RefMap<T extends { ref: Ref<T> }, V> = {
  _refMapItems: { [id: string]: V },
};

export function RefMap<T extends { ref: Ref<T> }, V>(): RefMap<T, V> {
  return { _refMapItems: {} };
}

export namespace RefMap {
  export function get<T extends { ref: Ref<T> }, V>(
    map: RefMap<T, V>,
    ref: Ref<T>,
  ): V | null {
    const item = map._refMapItems[ref.id];
    return item === undefined ? null : item;
  }

  export function set<T extends { ref: Ref<T> }, V>(
    map: RefMap<T, V>,
    ref: Ref<T>,
    value: V,
  ): RefMap<T, V> {
    return { _refMapItems: { ...map._refMapItems, [ref.id]: value } };
  }

  export function Refs<T extends { ref: Ref<T> }, V>(
    map: RefMap<T, V>,
  ): Ref<T>[] {
    return (Object
      .keys(map._refMapItems)
      .sort()
      .map(id => ({ id, _refTypeGuard: {} }))
    );
  }

  export function entries<T extends { ref: Ref<T> }, V>(
    map: RefMap<T, V>,
  ): [Ref<T>, V][] {
    return (Refs(map)
      .map(ref => [ref, map._refMapItems[ref.id]] as [Ref<T>, V])
    );
  }

  export function size<T extends { ref: Ref<T> }, V>(
    map: RefMap<T, V>,
  ): number {
    return Object.keys(map._refMapItems).length;
  }

  export function map<T extends { ref: Ref<T> }, VIn, VOut>(
    refMap: RefMap<T, VIn>,
    mapper: (input: VIn, ref: Ref<T>) => VOut,
  ): RefMap<T, VOut> {
    return Refs(refMap).reduce(
      (acc, ref) => {
        const oldValue = get(refMap, ref);

        if (oldValue === null) {
          return acc;
        }

        return set(acc, ref, mapper(oldValue, ref));
      },
      RefMap<T, VOut>(),
    );
  }

  export function equals<T extends { ref: Ref<T> }, V>(
    a: RefMap<T, V>,
    b: RefMap<T, V>,
    cmp: ((v1: V, v2: V) => boolean) = (v1, v2) => v1 === v2,
  ) {
    const aIds = ObjectKeys(a._refMapItems).sort();
    const bIds = ObjectKeys(b._refMapItems).sort();

    if (aIds.length !== bIds.length) {
      return false;
    }

    return range(aIds.length).every(i => (
      aIds[i] === bIds[i] &&
      cmp(a._refMapItems[aIds[i]], b._refMapItems[bIds[i]])
    ));
  }
}

export default RefMap;



// WEBPACK FOOTER //
// ./src/util/RefMap.ts